import { Rng } from '../core/rng';
import { clamp, Vec3 } from '../core/math';
import { MASTS, deckHalfWidth, deckY, halfBreadthXY, type MastId } from '../data/ship';
import { DIFFICULTIES, type DifficultyId, type ShipClass } from '../data/classes';
import { Ship } from './ship';
import { Battery } from './cannons';
import { DamageModel, SECTION_BOUNDS } from './damage';
import { FireSystem } from './fire';
import { Crew, type Orders, type Party } from './crew';
import { DeckCargo } from './deck';
import type { SimEvents } from './events';
import type { WaveField } from './waves';
import type { Wind } from './wind';
import type { Ballistics } from './ballistics';
import type { SailState } from './sails';
import type { Captain } from './captain';

export type Faction = 'player' | 'pirate' | 'navy' | 'merchant';
export type VesselState = 'active' | 'struck' | 'sinking' | 'sunk' | 'captured';

/** A mast (or part of one) gone by the board. */
export interface FallenSpar {
  mast: MastId;
  level: number;
  /** Seconds since it went. */
  age: number;
  /** Side it fell toward (+1 starboard). */
  side: number;
  /** Fall angle (0 upright .. ~PI/2 in the water). */
  angle: number;
  angVel: number;
  /** Design-space height of the break. */
  breakY: number;
  /** Cut away (drifting free) yet? */
  cutAway: boolean;
}

export interface WorldCtx {
  waves: WaveField;
  wind: Wind;
  ballistics: Ballistics;
  events: SimEvents;
  rain: number;
  time: number;
}

let nextId = 0;

export class Vessel {
  readonly id: number;
  readonly ship: Ship;
  readonly battery: Battery;
  readonly damage: DamageModel;
  readonly fire: FireSystem;
  readonly crew: Crew;
  readonly cargo: DeckCargo;
  captain: Captain | null = null;
  state: VesselState = 'active';
  /** Beat to quarters. */
  battle = false;
  threatSide = 0;
  threatRange = Infinity;
  /** Which broadside the gun crews are serving. */
  gunSide = 0;
  helm = 0;
  /** Vessel this one is grappled to / boarding. */
  boardingWith = -1;
  /** All hands called to the boarding fight (the guns and pumps left to look after themselves). */
  allHands = false;
  boardingSide = 0;
  readonly fallen: FallenSpar[] = [];
  /** Time since she started going down. */
  sinkTime = 0;
  /** Whose shot last struck her (-1 nobody's yet), and when (her clock). */
  lastHitBy = -1;
  lastHitAt = -Infinity;
  /**
   * How her people rate the fight they're in: our fighting power over the
   * enemy's (1 even; set by her captain, or for the player's ship by the
   * world). It sets the level their morale settles toward in battle.
   */
  odds = 1;
  /** Lights shown at night (lanterns lit). */
  lights = true;
  /** Damage parties ordered to work over the guns (the rest are left to her officers). */
  readonly orders: Orders = { leaks: false, fire: false, canvas: false, spars: false };
  /** Casks of grog left to serve out to the men, and how much is in them now (1 just served, fading). */
  grogCasks = 3;
  grog = 0;
  private readonly pending: { t: number; x: number; y: number; z: number; p: number }[] = [];
  private readonly rng: Rng;
  private readonly boxes = new Map<SailState, { frame: number; box: number[] }>();
  private frame = 0;
  private time = 0;

  constructor(readonly cls: ShipClass, readonly faction: Faction, readonly name: string, x: number, z: number, heading: number, seed: number, difficulty: DifficultyId = 'normal', plan = 3) {
    this.id = nextId++;
    this.rng = new Rng(seed);
    this.ship = new Ship(x, z, heading, plan, cls);
    this.battery = new Battery(cls, new Rng(seed + 1));
    this.damage = new DamageModel(cls);
    this.damage.vessel = this.id;
    this.fire = new FireSystem(seed + 2);
    const skill = faction === 'player' ? 0.08 : DIFFICULTIES[difficulty].crewSkill + (faction === 'navy' ? 0.08 : faction === 'merchant' ? -0.12 : 0);
    this.crew = new Crew(cls, seed + 3, skill);
    this.cargo = new DeckCargo(new Rng(seed + 4));
    // Merchants stow their guns' ammunition deep; everyone else is ready.
    if (faction === 'merchant') for (const g of this.battery.guns) g.loaded = false;
  }

  get alive(): boolean {
    return this.state !== 'sunk';
  }

  /** Can still fight: not struck, sinking or captured. */
  get fighting(): boolean {
    return this.state === 'active';
  }

  hitCtx(events: SimEvents): { ship: Ship; events: SimEvents; vessel: number } {
    return { ship: this.ship, events, vessel: this.id };
  }

  /** Axis-aligned design-space box of a sail's cloth (cached per step). */
  sailBox(s: SailState): number[] {
    let e = this.boxes.get(s);
    if (e && e.frame === this.frame) return e.box;
    const p = s.cloth.pos;
    const box = [Infinity, Infinity, Infinity, -Infinity, -Infinity, -Infinity];
    for (let i = 0; i < s.cloth.count; i += 2) {
      for (let k = 0; k < 3; k++) {
        const v = p[i * 3 + k]!;
        if (v < box[k]!) box[k] = v;
        if (v > box[k + 3]!) box[k + 3] = v;
      }
    }
    e = { frame: this.frame, box };
    this.boxes.set(s, e);
    return box;
  }

  onMastHit(id: MastId, amount: number, yd: number, side: number, where: Vec3, events: SimEvents): void {
    const lost = this.damage.damageMast(id, amount, yd, events, side, where);
    if (lost > 0) this.dismast(id, lost, side, yd, events);
  }

  private dismast(id: MastId, lost: number, side: number, yd: number, events: SimEvents): void {
    const level = lost === 3 ? 0 : lost === 2 ? 1 : 2;
    this.ship.sails.loseMast(id, level);
    this.shock(level === 0 ? 0.12 : 0.04);
    // A lower mast takes the headsails' stays with it when it's the foremast.
    if (id === 'fore' && level <= 1) for (const s of this.ship.sails.sails) if (s.spec.kind === 'foreaft' && !s.spec.peak && s.mount.level >= level) s.lost = true;
    if (id === 'main' && level <= 2) this.ship.sails.flags.forEach((f) => (f.lost = true));
    this.crew.loseAloft(id, this.hitCtx(events));
    this.fallen.push({ mast: id, level, age: 0, side, angle: 0.02, angVel: 0.05, breakY: yd, cutAway: false });
    const md = this.damage.masts[id];
    if (md) {
      md.wreck++;
      md.jury = 0;
    }
    // Wreckage alongside drags her round until it's cut away.
    this.damage.wreckDrag += level === 0 ? 0.6 : 0.2;
    // The falling spars smash what's below.
    const spec = MASTS.find((m) => m.id === id)!;
    const x = spec.x, hw = deckHalfWidth(x);
    this.damage.breachRail(x + this.rng.range(-2, 2), side, level === 0 ? 2.5 : 1);
    this.crew.strike(x, deckY(x) + 1, side * hw * 0.6, level === 0 ? 4 : 2.5, 0.5, this.hitCtx(events));
  }

  /** Detonation at a design-space point (shell, powder, magazine). */
  explode(x: number, y: number, z: number, power: number, events: SimEvents): void {
    const ship = this.ship;
    const wp = ship.designToWorld(tmpA.set(x, y, z), tmpA);
    events.push({ type: 'explosion', x: wp.x, y: wp.y, z: wp.z, size: power, vessel: this.id });
    const radius = 1.8 + power * 1.4;
    for (let sec = 0; sec < 5; sec++) {
      const cx = (SECTION_BOUNDS[sec]! + SECTION_BOUNDS[sec + 1]!) / 2;
      const d = Math.abs(cx - x);
      if (d > radius + 4) continue;
      const f = 1 - clamp(d / (radius + 4), 0, 1);
      for (const side of [-1, 1]) {
        const lateral = 1 - clamp(Math.abs(side * 3 - z) / 8, 0, 1);
        this.damage.damageHull(sec, side, 22 * power * f * (0.4 + lateral), events, wp);
      }
      this.damage.deck[sec]! -= 20 * power * f;
      this.damage.structure[sec]! -= 12 * power * power * f;
    }
    // Blown-out planking.
    const holes = Math.round(power * (power > 3 ? 2 : 1));
    for (let i = 0; i < holes; i++) {
      const side = z === 0 ? (this.rng.chance(0.5) ? 1 : -1) : Math.sign(z);
      const hx = x + this.rng.range(-radius, radius) * 0.6;
      const hy = clamp(y + this.rng.range(-1, 0.5), -2.5, deckY(hx));
      const hb = halfBreadthXY(hx, hy);
      if (hb > 0) this.damage.addHole(hx, hy, side * hb, 0.18 * Math.sqrt(power) + this.rng.range(0, 0.15), true);
    }
    this.damage.breachRail(x, Math.sign(z) || 1, 0.8 * power);
    this.crew.strike(x, y, z, radius + 1.5, clamp(0.35 + power * 0.08, 0, 0.95), this.hitCtx(events));
    this.fire.ignite(x, y, z, clamp(0.35 * power, 0.2, 1), ship, events, this.id);
    for (let i = 0; i < power * 2; i++) this.fire.ignite(x + this.rng.range(-radius, radius), y, z + this.rng.range(-2, 2), 0.2, ship, events, this.id);
    for (const m of MASTS) {
      if (!this.damage.masts[m.id] || Math.abs(m.x - x) > radius + 1) continue;
      this.onMastHit(m.id, 12 * power, deckY(m.x) + 2, this.rng.chance(0.5) ? 1 : -1, wp, events);
    }
    ship.applyImpulse(0, 4000 * power, 0, wp.x, wp.y, wp.z);
  }

  /** Her own clock, s (for when things happened to her). */
  get clock(): number {
    return this.time;
  }

  /** A blow to her people's heart (a mast lost, her magazine gone up), 0..1. */
  /** Heart put into her people: shot of ours striking home, a rival's mast down (`feel`: the swing of the fight). */
  cheer(amount: number, feel = 0): void {
    this.crew.morale = clamp(this.crew.morale + amount, 0, 1);
    this.crew.momentum += feel;
  }

  /** Order a damage party to it over the guns, or leave it to her officers again. */
  order(party: Party, on = !this.orders[party]): void {
    this.orders[party] = on;
  }

  /**
   * Serve out grog: heart for men who've lost it (little for men already in
   * good spirits), but for a minute and a half the gun captains' hands are
   * less steady. One cask at a time, and only so many aboard.
   */
  rally(events: SimEvents): boolean {
    if (this.grogCasks <= 0 || this.grog > 0.2 || !this.fighting) return false;
    this.grogCasks--;
    const lift = clamp(0.04 + 0.25 * (0.8 - this.crew.morale), 0.03, 0.17);
    this.cheer(lift, 0.15);
    this.grog = 1;
    events.push({ type: 'rally', vessel: this.id, left: this.grogCasks, lift });
    return true;
  }

  shock(amount: number): void {
    this.crew.morale = clamp(this.crew.morale - amount, 0, 1);
  }

  /** Captain / player intent for this step. */
  update(dt: number, w: WorldCtx, helm: number): void {
    this.frame++;
    this.time += dt;
    const ship = this.ship;
    const ev = w.events;
    if (this.state === 'sunk') return;

    // Delayed secondary explosions (loose powder, shot lockers).
    for (let i = this.pending.length - 1; i >= 0; i--) {
      const p = this.pending[i]!;
      p.t -= dt;
      if (p.t <= 0) {
        this.pending.splice(i, 1);
        this.explode(p.x, p.y, p.z, p.p, ev);
      }
    }
    // Magazine: heat from fire or a lucky shot sets it off.
    const mag = this.damage.magazine;
    if (!mag.exploded && mag.heat >= 1) {
      mag.exploded = true;
      this.shock(0.4);
      this.explode(mag.x, mag.y, 0, 6, ev);
      for (let i = 0; i < 4; i++) {
        this.pending.push({ t: this.rng.range(0.3, 3), x: mag.x + this.rng.range(-8, 8), y: deckY(mag.x) + this.rng.range(-1.5, 0.8), z: this.rng.range(-3, 3), p: this.rng.range(0.6, 1.6) });
      }
      for (const c of ship.compartments) c.water = Math.min(c.capacity, c.water + c.capacity * 0.3);
    }

    // State: sinking, abandoned, struck.
    const deckAwash = ship.flooding > 0.95 || this.damage.brokenBack || mag.exploded;
    if (this.state !== 'sinking' && deckAwash) {
      this.state = 'sinking';
      ev.push({ type: 'shipSinking', vessel: this.id, x: ship.com.x, z: ship.com.z });
    }
    if (this.state === 'sinking') {
      this.sinkTime += dt;
      this.battle = false;
    }
    if (this.state === 'active' && this.faction !== 'player' && !this.captain && this.crew.morale < 0.12 && (this.damage.integrity < 0.5 || this.crew.strength < 0.35 || ship.sails.masts.length < this.cls.masts.length)) {
      this.strike(ev);
    }

    // The crew: task planning and work.
    const threatLocal = this.threatSide;
    this.crew.update(dt, {
      ship, damage: this.damage, fire: this.fire, guns: this.battery.guns, battle: this.battle && this.fighting, odds: this.odds,
      threatSide: threatLocal, threatRange: this.threatRange, gunSide: this.gunSide,
      abandon: this.state === 'sinking', surrender: this.state === 'struck' || this.state === 'captured',
      boarding: this.boardingWith >= 0 ? this.boardingSide : 0, allHands: this.allHands && this.boardingWith >= 0, orders: this.orders, grog: this.grog,
      events: ev, vessel: this.id, waterHeight: (x, z) => w.waves.heightAt(x, z, true),
    });
    ship.helmCrew = this.crew.helmCrew;
    ship.sails.handling = Math.max(this.crew.sailHands, this.battle ? 0.1 : 0.5);
    ship.pumpRate = this.crew.pumpers * 0.011 * this.ship.sx;
    this.battery.gunnery = this.crew.gunnery;
    this.grog = Math.max(0, this.grog - dt / 90);
    this.battery.unsteady = 1 + 0.9 * this.grog;

    ship.step(dt, w.waves, w.wind, { helm }, ev, 0.2);
    const q = 0.5 * 1.225 * w.wind.speed * w.wind.speed;
    this.damage.update(dt, ship, q, ev);
    const aw = ship.apparentWind;
    this.fire.update(dt, ship, this.damage, w.rain, aw.x, aw.z, ev, this.id);
    if (this.fighting || this.state === 'struck') this.battery.update(dt, ship, w.ballistics, ev, this.id);
    this.cargo.update(dt, ship.gLocal, ship.deckWater, ship.deckWaterSide, 0, ev, (p, out) => ship.designToWorld(p, out));
    this.updateFallen(dt);

    if (ship.sunk) {
      this.state = 'sunk';
      ev.push({ type: 'shipSunk', vessel: this.id, x: ship.com.x, z: ship.com.z, cls: this.cls.id });
    }
  }

  strike(events: SimEvents): void {
    if (this.state !== 'active') return;
    this.state = 'struck';
    this.ship.sails.struck = true;
    this.ship.sails.setPlan(0);
    this.battle = false;
    events.push({ type: 'struck', vessel: this.id });
  }

  /** Masts falling over the side, then cut away by the crew. */
  private updateFallen(dt: number): void {
    for (const f of this.fallen) {
      f.age += dt;
      if (f.angle < 1.45) {
        f.angVel += (Math.sin(f.angle) * 2.2 + 0.4) * dt;
        f.angle = Math.min(1.45, f.angle + f.angVel * dt);
      } else if (!f.cutAway && f.age > 25 && this.crew.effective > 4) {
        f.cutAway = true;
        const md = this.damage.masts[f.mast];
        if (md) md.wreck = Math.max(0, md.wreck - 1);
        this.damage.wreckDrag = Math.max(0, this.damage.wreckDrag - (f.level === 0 ? 0.6 : 0.2));
      }
    }
  }
}

const tmpA = new Vec3();
